import React, { useState, useEffect, useRef } from 'react';
import * as St from './styles';

export interface IOption {
  readonly id?: string | number;
  value: string;
  label?: string;
}

export type AutoCompleteSelectMultipleProps = {
  name?: string;
  id?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string;
  options: IOption[];
  value?: IOption[];
  onChange?: (options: IOption[]) => void;
  onFocus?: (isOpen: boolean) => void;
  readOnly?: boolean;
  heightWhenOpen?: string;
  continerOptionsStyle?: React.CSSProperties;
  showChips?: boolean;
  btnClear?: boolean;
};

const AutoCompleteSelectMultiple = ({
  label,
  placeholder = 'Selecione...',
  value = [],
  errorMessage,
  onChange,
  readOnly,
  options = [],
  disabled,
  heightWhenOpen = '250px',
  continerOptionsStyle,
  onFocus,
  name,
  showChips = true,
  btnClear = true,
}: AutoCompleteSelectMultipleProps): JSX.Element => {
  const [isFocused, setIsFocused] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<IOption[]>(options);
  const [selectedOptions, setSelectedOptions] = useState<IOption[]>(value);
  const [filterText, setFilterText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sincroniza valor externo
  useEffect(() => {
    setSelectedOptions(value || []);
  }, [value]);

  // Sincroniza opções quando mudam
  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  // Notifica quando o foco muda
  useEffect(() => {
    if (onFocus) {
      onFocus(isFocused);
    }
  }, [isFocused, onFocus]);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    if (isFocused) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isFocused]);

  const handleToggleFocus = () => {
    if (!readOnly && !disabled) {
      setIsFocused((prev) => !prev);
      if (!isFocused) {
        inputRef.current?.focus();
      }
    }
  };

  const handleSelectOption = (option: IOption) => {
    const isAlreadySelected = selectedOptions.some(
      (o) => o.value === option.value,
    );

    let newSelectedOptions: IOption[];

    if (isAlreadySelected) {
      newSelectedOptions = selectedOptions.filter(
        (o) => o.value !== option.value,
      );
    } else {
      newSelectedOptions = [...selectedOptions, option];
    }

    setSelectedOptions(newSelectedOptions);

    if (onChange) {
      onChange(newSelectedOptions);
    }

    // Limpa o filtro
    setFilterText('');
    setFilteredOptions(options);
  };

  const handleFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: filterValue } = e.target;
    setFilterText(filterValue);

    if (!filterValue.trim()) {
      setFilteredOptions(options);
      return;
    }

    const filtered = options.filter((option) =>
      option.value
        .toLowerCase()
        .replaceAll(' ', '')
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .trim()
        .includes(filterValue.toLowerCase().replaceAll(' ', '').trim()),
    );

    setFilteredOptions(filtered);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOptions([]);
    setFilterText('');

    if (onChange) {
      onChange([]);
    }
  };

  const isOptionSelected = (option: IOption) =>
    selectedOptions.some((o) => o.value === option.value);

  return (
    <St.Container ref={containerRef}>
      {label && <label htmlFor={name}>{label}</label>}

      <St.SelectButton
        disabled={disabled}
        isFocused={isFocused}
        onClick={handleToggleFocus}
        data-testid="select-button"
      >
        <St.SelectContent>
          {selectedOptions.length > 0 ? (
            showChips ? (
              <St.ChipsContainer>
                {selectedOptions.map((option) => (
                  <St.Chip
                    key={option.id || option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectOption(option);
                    }}
                  >
                    {option.label || option.value}
                    <St.ChipRemoveButton>×</St.ChipRemoveButton>
                  </St.Chip>
                ))}
              </St.ChipsContainer>
            ) : (
              <span>{selectedOptions.length} selecionado(s)</span>
            )
          ) : (
            <St.Placeholder>{placeholder}</St.Placeholder>
          )}
        </St.SelectContent>

        {btnClear && selectedOptions.length > 0 && (
          <St.ClearButton
            onClick={handleClear}
            type="button"
            aria-label="Limpar seleção"
          >
            ✕
          </St.ClearButton>
        )}

        <St.IconRight
          isFocused={isFocused}
          src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E"
          alt="chevron"
        />
      </St.SelectButton>

      {isFocused && (
        <St.DropdownContainer style={continerOptionsStyle}>
          <St.SearchInput
            ref={inputRef}
            type="text"
            placeholder="Pesquisar..."
            value={filterText}
            onChange={handleFilter}
            onClick={(e) => e.stopPropagation()}
            disabled={disabled}
            readOnly={readOnly}
          />

          <St.OptionsList style={{ maxHeight: heightWhenOpen }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = isOptionSelected(option);
                return (
                  <St.OptionItem
                    key={option.id || option.value}
                    isSelected={isSelected}
                    onClick={() => handleSelectOption(option)}
                  >
                    <St.CheckBox
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      aria-label={`Selecionar ${option.label || option.value}`}
                    />
                    <span>{option.label || option.value}</span>
                  </St.OptionItem>
                );
              })
            ) : (
              <St.NoResults>Nenhuma opção encontrada</St.NoResults>
            )}
          </St.OptionsList>
        </St.DropdownContainer>
      )}

      {errorMessage && <St.ErrorMessage>{errorMessage}</St.ErrorMessage>}
    </St.Container>
  );
};

export default AutoCompleteSelectMultiple;
